import React from 'react';
import type { MarketAnalyticsSnapshot } from '../../types/marketAnalytics';
import { useLanguage } from '../../contexts/LanguageContext';
import { regimeMeta, RISK_LEVEL_COLORS } from '../../utils/regimeTheme';
import GexAnalyticsStrip from './GexAnalyticsStrip';
import BreedenLitzenbergerChart from './BreedenLitzenbergerChart';
import { RegimeSnapshotProvider } from './RegimeSnapshotBridge';

export type MarketAnalyticsGlanceProps = {
  snapshot: MarketAnalyticsSnapshot | null;
  loading?: boolean;
  showRegime?: boolean;
  showGexStrip?: boolean;
  showBlPredictors?: boolean;
  showBlChart?: boolean;
  chartHeight?: number;
  compact?: boolean;
};

/** Regime + Breeden-Litzenberger predictors for first-glance / overlay panels. */
const MarketAnalyticsGlance: React.FC<MarketAnalyticsGlanceProps> = ({
  snapshot,
  loading = false,
  showRegime = true,
  showGexStrip = true,
  showBlPredictors = true,
  showBlChart = true,
  chartHeight = 220,
  compact = false,
}) => {
  const { language } = useLanguage();
  const ar = language === 'ar';

  if (!snapshot && !loading) {
    return (
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, textAlign: 'center' }}>
        {ar
          ? 'تحليلات النظام و BL غير متوفرة — تحتاج بيانات سلسلة خيارات.'
          : 'Regime & BL analytics need option chain data.'}
      </p>
    );
  }

  const meta = snapshot ? regimeMeta(snapshot.regime) : regimeMeta('CALM');
  const rnd = snapshot?.rnd;

  return (
    <RegimeSnapshotProvider snapshot={snapshot}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 14 }}>
        {showRegime && snapshot && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontSize: compact ? 24 : 28, lineHeight: 1 }} aria-hidden>
              {meta.emoji}
            </span>
            <div>
              <div style={{ fontWeight: 600, fontSize: compact ? 13 : 14, color: meta.accent }}>
                {ar ? meta.labelAr : meta.labelEn}
                {loading ? ' …' : ''}
              </div>
              <div style={{ fontSize: 12, color: 'var(--txt-2)' }}>
                {ar ? 'مخاطر' : 'Risk'}{' '}
                <span style={{ color: RISK_LEVEL_COLORS[snapshot.risk_level] }}>
                  {snapshot.risk_level}
                </span>
                {' · '}
                {Math.round(snapshot.regime_confidence * 100)}% {ar ? 'ثقة' : 'conf'}
                {snapshot.gex.trap_zone && (
                  <span style={{ color: 'var(--down)', marginLeft: 8 }}>
                    {ar ? '⚠ فخ جاما' : '⚠ gamma trap'}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {showGexStrip && <GexAnalyticsStrip />}

        {showBlPredictors && rnd && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 8,
            }}
          >
            {[
              {
                label: ar ? 'أمام BL' : 'BL forward',
                value: `${rnd.forward.toFixed(2)} (${rnd.forward_pct >= 0 ? '+' : ''}${rnd.forward_pct.toFixed(2)}%)`,
              },
              {
                label: ar ? 'الانحراف' : 'Skew',
                value: rnd.skewness.toFixed(2),
              },
              {
                label: ar ? 'التفرطح' : 'Kurtosis',
                value: rnd.kurtosis.toFixed(2),
              },
              {
                label: ar ? 'ذيل' : 'Tail ratio',
                value: rnd.tail_ratio.toFixed(2),
              },
              {
                label: ar ? 'انحياز BL' : 'BL skew bias',
                value: rnd.regime_skew,
              },
              {
                label: ar ? 'تثبيت' : 'Pin %',
                value:
                  snapshot?.gex.pin_probability != null
                    ? `${Math.round(snapshot.gex.pin_probability * 100)}%`
                    : '—',
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--hair)',
                  background: 'var(--elev2)',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted-2)', textTransform: 'uppercase' }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, color: 'var(--txt)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{item.value}</div>
              </div>
            ))}
          </div>
        )}

        {showBlChart && (
          <div
            style={{
              borderRadius: 10,
              border: '1px solid var(--hair)',
              background: 'var(--elev)',
              padding: compact ? 8 : 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--txt)' }}>
              {ar ? 'كثافة مخاطرة محايدة (بريدن-ليتزنبرجر)' : 'Breeden-Litzenberger RND'}
            </div>
            <BreedenLitzenbergerChart snapshot={snapshot} height={chartHeight} />
          </div>
        )}
      </div>
    </RegimeSnapshotProvider>
  );
};

export default MarketAnalyticsGlance;
